import { lazy } from 'react';

// Lazy-loaded page imports
const HomeDispatcher = lazy(() => import('./pages/HomeDispatcher'));
const VenusHome = lazy(() => import('./pages/VenusHome'));
const VenusLibrary = lazy(() => import('./pages/VenusLibrary'));
const VenusMedia = lazy(() => import('./pages/VenusMedia'));
const VenusInsights = lazy(() => import('./pages/VenusInsights'));
const VenusInsightDetail = lazy(() => import('./pages/VenusInsightDetail'));
const VenusAbout = lazy(() => import('./pages/VenusAbout'));
const VenusMasterclass = lazy(() => import('./pages/VenusMasterclass'));
const VenusNeuroHeal = lazy(() => import('./pages/VenusNeuroHeal'));
const Pricing = lazy(() => import('./pages/Pricing'));
const Trust = lazy(() => import('./pages/Trust'));
const FeaturesIndex = lazy(() => import('./pages/FeaturesIndex'));
const FeatureDetail = lazy(() => import('./pages/FeatureDetail'));
const AffiliateRedirect = lazy(() => import('./pages/AffiliateRedirect'));
const AffiliateApply = lazy(() => import('./pages/AffiliateApply'));
const AffiliateDashboard = lazy(() => import('./pages/AffiliateDashboard'));
const AdminInsights = lazy(() => import('./pages/AdminInsights'));
const Auth = lazy(() => import('./pages/Auth'));
const Onboarding = lazy(() => import('./pages/Onboarding'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Haven = lazy(() => import('./pages/Haven'));
const Patterns = lazy(() => import('./pages/Patterns'));
const PatternLibrary = lazy(() => import('./pages/PatternLibrary'));
const Exercises = lazy(() => import('./pages/Exercises'));
const RealityTracker = lazy(() => import('./pages/RealityTracker'));
const Community = lazy(() => import('./pages/Community'));
const Journal = lazy(() => import('./pages/Journal'));
const Experiments = lazy(() => import('./pages/Experiments'));
const FrequencyGenerator = lazy(() => import('./pages/FrequencyGenerator'));
const CheckIn = lazy(() => import('./pages/CheckIn'));
const CheckInHistory = lazy(() => import('./pages/CheckInHistory'));
const JourneyMemories = lazy(() => import('./pages/JourneyMemories'));
const LeyLines = lazy(() => import('./pages/LeyLines'));
const EnergyScan = lazy(() => import('./pages/EnergyScan'));
const HolisticHealing = lazy(() => import('./pages/HolisticHealing'));
const HealthTracking = lazy(() => import('./pages/HealthTracking'));
const GuidedProtocols = lazy(() => import('./pages/GuidedProtocols'));
const Biofeedback = lazy(() => import('./pages/Biofeedback'));
const Leaderboard = lazy(() => import('./pages/Leaderboard'));
const HealingIntelligence = lazy(() => import('./pages/HealingIntelligence'));
const DailyRitual = lazy(() => import('./pages/DailyRitual'));
const NutritionGuide = lazy(() => import('./pages/NutritionGuide'));
const Settings = lazy(() => import('./pages/Settings'));
const SensorHub = lazy(() => import('./pages/SensorHub'));
const CrystalLiberation = lazy(() => import('./pages/CrystalLiberation'));
const DreamJournal = lazy(() => import('./pages/DreamJournal'));
const ConsciousnessScore = lazy(() => import('./pages/ConsciousnessScore'));
const FrequencyPrescription = lazy(() => import('./pages/FrequencyPrescription'));
const BinauralGenerator = lazy(() => import('./pages/BinauralGenerator'));
const CrystalCombinations = lazy(() => import('./pages/CrystalCombinations'));
const AstrologicalAlignment = lazy(() => import('./pages/AstrologicalAlignment'));
const HRVMonitor = lazy(() => import('./pages/HRVMonitor'));
const MeditationScripts = lazy(() => import('./pages/MeditationScripts'));
const MilestoneSharing = lazy(() => import('./pages/MilestoneSharing'));
const EEGMonitor = lazy(() => import('./pages/EEGMonitor'));
const SacredCeremonies = lazy(() => import('./pages/SacredCeremonies'));
const MindGames = lazy(() => import('./pages/MindGames'));
const Features = lazy(() => import('./pages/Features'));
const Subscription = lazy(() => import('./pages/Subscription'));
const HelpSupport = lazy(() => import('./pages/HelpSupport'));
const PracticePatterns = lazy(() => import('./pages/PracticePatterns'));
const WeeklyReport = lazy(() => import('./pages/WeeklyReport'));
const AdminPanel = lazy(() => import('./pages/AdminPanel'));
const Practices = lazy(() => import('./pages/Practices'));
const DreamInsights = lazy(() => import('./pages/DreamInsights'));
const VsCalm = lazy(() => import('./pages/VsCalm'));
const VsHeadspace = lazy(() => import('./pages/VsHeadspace'));
const VsMetawise = lazy(() => import('./pages/VsMetawise'));

// Route definitions: [path, Component, guard, requireTier?]
// guard: 'public' | 'private' | 'paywall'
// requireTier (only meaningful when guard === 'paywall'):
//   - undefined  : any active access works (trial or paid)
//   - 'core'     : require Core or above (Explorer trial users will be upgraded)
//   - 'premium'  : require Premium or above (premium_trial / paid premium / founder / creator)
const routes = [
  // Two-domain root dispatcher:
  // - neurohealhaven.com / → NeuroHeal Landing (install/sign-up entry)
  // - venusglowhaven.com / → Venus Glow Haven umbrella homepage
  // - preview/dev /        → Venus Glow Haven umbrella homepage
  ['/', HomeDispatcher, 'public'],
  // Venus Glow Haven umbrella surface (marketing) — venusglowhaven.com
  ['/venus', VenusHome, 'public'],
  ['/library', VenusLibrary, 'public'],
  ['/media', VenusMedia, 'public'],
  ['/insights', VenusInsights, 'public'],
  ['/insights/:slug', VenusInsightDetail, 'public'],
  ['/about', VenusAbout, 'public'],
  ['/masterclass', VenusMasterclass, 'public'],
  // Public affiliate program landing + application form. Must come BEFORE
  // the catch-all /:code so it isn't intercepted by AffiliateRedirect.
  ['/affiliate', AffiliateApply, 'public'],
  ['/pricing', Pricing, 'public'],
  ['/trust', Trust, 'public'],
  // Public feature marketing pages — index + per-feature detail (13 slugs).
  ['/features', FeaturesIndex, 'public'],
  ['/features/:slug', FeatureDetail, 'public'],
  // Comparison pages targeting competitor search intent (D5.3).
  ['/vs/calm', VsCalm, 'public'],
  ['/vs/headspace', VsHeadspace, 'public'],
  ['/vs/metawise', VsMetawise, 'public'],
  // Logged-in affiliate self-service dashboard. Uses 'private' guard so
  // anyone signed in can hit it; the endpoint returns 404 when the caller
  // isn't an approved partner and the page renders an empty-state CTA.
  ['/affiliate/dashboard', AffiliateDashboard, 'private'],
  // NeuroHeal landing — canonical app intro, also reachable via /
  ['/neuroheal', VenusNeuroHeal, 'public'],
  // Founder affiliate URL handler (e.g. /unveiled30, /nheal30, /healwithsarah30)
  // Must come AFTER all named umbrella paths so they take precedence.
  ['/:code', AffiliateRedirect, 'public'],
  ['/auth', Auth, 'public'],
  ['/admin/insights', AdminInsights, 'private'],
  ['/subscription', Subscription, 'private'],
  ['/subscription/success', Subscription, 'private'],
  ['/help', HelpSupport, 'private'],
  ['/onboarding', Onboarding, 'private'],
  ['/settings', Settings, 'private'],
  ['/admin', AdminPanel, 'private'],
  ['/dashboard', Dashboard, 'paywall'],
  ['/haven', Haven, 'paywall'],
  ['/patterns', Patterns, 'paywall'],
  ['/pattern-library', PatternLibrary, 'paywall'],
  ['/journal', Journal, 'paywall'],
  ['/experiments', Experiments, 'paywall'],
  ['/exercises', Exercises, 'paywall'],
  ['/reality-tracker', RealityTracker, 'paywall'],
  ['/community', Community, 'paywall', 'core'],
  ['/frequency', FrequencyGenerator, 'paywall'],
  ['/sound-healing', FrequencyGenerator, 'paywall'],
  ['/check-in', CheckIn, 'paywall'],
  ['/checkin-history', CheckInHistory, 'paywall'],
  ['/journey', JourneyMemories, 'paywall'],
  ['/ley-lines', LeyLines, 'paywall'],
  ['/earth-grid', LeyLines, 'paywall'],
  ['/energy-scan', EnergyScan, 'paywall', 'premium'],
  ['/holistic-healing', HolisticHealing, 'paywall'],
  ['/health-tracking', HealthTracking, 'paywall'],
  ['/guided-protocols', GuidedProtocols, 'paywall'],
  ['/biofeedback', Biofeedback, 'paywall', 'premium'],
  ['/leaderboard', Leaderboard, 'paywall'],
  ['/healing-intelligence', HealingIntelligence, 'paywall', 'premium'],
  ['/daily-ritual', DailyRitual, 'paywall'],
  ['/nutrition', NutritionGuide, 'paywall'],
  ['/sensor-hub', SensorHub, 'paywall', 'core'],
  ['/crystal-liberation', CrystalLiberation, 'paywall'],
  ['/dream-journal', DreamJournal, 'paywall'],
  ['/consciousness-score', ConsciousnessScore, 'paywall'],
  ['/frequency-prescription', FrequencyPrescription, 'paywall', 'core'],
  ['/binaural', BinauralGenerator, 'paywall', 'core'],
  ['/crystal-combinations', CrystalCombinations, 'paywall', 'core'],
  ['/astrology', AstrologicalAlignment, 'paywall'],
  ['/hrv-monitor', HRVMonitor, 'paywall', 'premium'],
  ['/meditation-scripts', MeditationScripts, 'paywall'],
  ['/milestones', MilestoneSharing, 'paywall', 'core'],
  ['/eeg-monitor', EEGMonitor, 'paywall', 'premium'],
  ['/ceremonies', SacredCeremonies, 'paywall', 'premium'],
  ['/mind-games', MindGames, 'paywall'],
  ['/practices', Practices, 'paywall'],
  ['/breathwork', GuidedProtocols, 'paywall'],
  ['/dream-insights', DreamInsights, 'paywall'],
  ['/all-features', Features, 'paywall'],
  ['/practice-patterns', PracticePatterns, 'paywall'],
  ['/weekly-report', WeeklyReport, 'paywall', 'core'],
];

export default routes;
